import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Button,
  Avatar,
  TableContainer,
  Paper,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Menu,
  Badge,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import './UpcomingSessionTable.css';
import '../../SessionTable/SessionTable.css';
import OneOnOneSessionIcon from '../../../assets/Dashboard/oneoneonesession.svg';
import GroupSessionIcon from '../../../assets/Dashboard/group.svg';
import PrimeSessionIcon from '../../../assets/Dashboard/prime.svg';
import FilterIcon from '../../../assets/FiltersIcon/filter.svg';
import { useTranslation } from '../../../i18n/LanguageContext';
import { styled } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import usePagination from '@mui/material/usePagination';
import { useNavigate } from 'react-router-dom';

const SessionTypeColor = {
  one_on_one: {
    color: '#0A5EF3',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneSessionIcon,
  },
  group: {
    color: '#7BCF8C',
    bgcolor: '#FAFFFB',
    value: 'Group',
    icon: GroupSessionIcon,
  },
  prime: {
    color: '#AD8B00',
    bgcolor: '#F0EFFF',
    value: 'Prime',
    icon: PrimeSessionIcon,
  },
};
const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '10px 0',
  display: 'flex',
  justifyContent: 'center',
});

const formatLevel = level => {
  if (!level) return 'N/A';
  return level.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const UpcomingSessionTable = ({ sessions }) => {
  const navigate = useNavigate();

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionTypeFilter, setSessionTypeFilter] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const rowsPerPage = 5;

  // Apply search and filters
  useEffect(() => {
    let filtered = sessions;

    // Search filter (by learner name or subject)
    if (searchQuery) {
      filtered = filtered.filter(
        session =>
          session.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          session.subject.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Session type filter
    if (sessionTypeFilter !== 'all') {
      filtered = filtered.filter(session => {
        const sessionType = session.sessionType.toLowerCase().replace(/-/g, '_').replace(/ /g, '_');
        return sessionType === sessionTypeFilter;
      });
    }

    setFilteredSessions(filtered);
    setPage(1); // Reset to first page when filters change
  }, [sessions, searchQuery, sessionTypeFilter]);

  const { items } = usePagination({
    count: Math.ceil(filteredSessions.length / rowsPerPage),
    onChange: (e, v) => setPage(v),
  });
  const startIndex = (page - 1) * rowsPerPage;
  const currentRows = filteredSessions.slice(startIndex, startIndex + rowsPerPage);

  // Handle search change
  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  // Handle filter icon click
  const handleFilterClick = event => {
    setFilterAnchorEl(event.currentTarget);
  };

  // Handle filter menu close
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Handle session type filter change
  const handleSessionTypeFilterChange = filterValue => {
    setSessionTypeFilter(filterValue);
    handleFilterClose();
  };

  // Handle click on a table row
  const handleCellClick = (event, id) => {
    // Check if the click is from the actions column
    const isActionsCell = event.target.closest('td:last-child');
    if (!isActionsCell) {
      navigate(`/instructor/sessions-details/${id}`);
    }
  };

  return (
    <Box className="upcoming-sessions-container">
      {/* Title, Search and Filter on Same Row */}
      <Box className="header-with-search-filter">
        <Typography variant="h6" className="upcoming-sessions-title">
          Upcoming Sessions
        </Typography>

        <Box className="search-filter-wrapper">
          <TextField
            placeholder="Search by learner name or subject..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            sx={{
              width: '400px',
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Poppins',
                fontSize: '14px',
                borderRadius: '20px',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#949DA6' }} />
                </InputAdornment>
              ),
            }}
          />

          <Badge
            color="primary"
            variant="dot"
            invisible={sessionTypeFilter === 'all'}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#0A5EF3',
                right: 8,
                top: 8,
              },
            }}
          >
            <IconButton
              onClick={handleFilterClick}
              className="filter-icon-button"
              sx={{
                backgroundColor: '#F5F6F7',
                borderRadius: '12px',
                padding: '10px',
                '&:hover': {
                  backgroundColor: '#E8F0FF',
                },
              }}
            >
              <Box component="img" src={FilterIcon} alt="Filter" sx={{ width: 20, height: 20 }} />
            </IconButton>
          </Badge>

          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={handleFilterClose}
            PaperProps={{
              sx: {
                marginTop: '8px',
                borderRadius: '8px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                minWidth: '180px',
              },
            }}
          >
            <MenuItem
              onClick={() => handleSessionTypeFilterChange('all')}
              selected={sessionTypeFilter === 'all'}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                '&.Mui-selected': {
                  backgroundColor: '#E8F0FF',
                  color: '#0A5EF3',
                  fontWeight: 500,
                },
              }}
            >
              All Types
            </MenuItem>
            <MenuItem
              onClick={() => handleSessionTypeFilterChange('one_on_one')}
              selected={sessionTypeFilter === 'one_on_one'}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                '&.Mui-selected': {
                  backgroundColor: '#E8F0FF',
                  color: '#0A5EF3',
                  fontWeight: 500,
                },
              }}
            >
              One-on-One
            </MenuItem>
            <MenuItem
              onClick={() => handleSessionTypeFilterChange('group')}
              selected={sessionTypeFilter === 'group'}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                '&.Mui-selected': {
                  backgroundColor: '#E8F0FF',
                  color: '#0A5EF3',
                  fontWeight: 500,
                },
              }}
            >
              Group
            </MenuItem>
            <MenuItem
              onClick={() => handleSessionTypeFilterChange('prime')}
              selected={sessionTypeFilter === 'prime'}
              sx={{
                fontFamily: 'Poppins',
                fontSize: '14px',
                '&.Mui-selected': {
                  backgroundColor: '#E8F0FF',
                  color: '#0A5EF3',
                  fontWeight: 500,
                },
              }}
            >
              Prime
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ marginTop: 2, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 650 }} aria-label="upcoming sessions table">
          <TableHead
            sx={{
              backgroundColor: '#EEF4FF',
              '& th': {
                color: 'var(--Text, #14171F)',
                fontFamily: 'Poppins',
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: 'normal',
                padding: '10px 16px',
              },
            }}
          >
            <TableRow>
              <TableCell>Learner Name</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Timing</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>School Level</TableCell>
              <TableCell>Session Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': {
                color: 'var(--Text, #14171F)',
                fontFamily: 'Poppins',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: 'normal',
                padding: '9px 16px',
              },
              '& tr:hover': {
                backgroundColor: '#E9F1FF',
                cursor: 'pointer',
              },
            }}
          >
            {currentRows.length > 0 ? (
              currentRows.map(session => {
                // Determine type key for mapping
                const key = session.sessionType.toLowerCase().replace(/-/g, '_').replace(/ /g, '_');
                const typeInfo = SessionTypeColor[key] || {};
                return (
                  <TableRow
                    key={session.id}
                    className="session-row"
                    onClick={event => handleCellClick(event, session.id)}
                  >
                    <TableCell>
                      <Box className="student-cell">
                        <Avatar
                          src={session.avatarUrl}
                          alt={session.studentName}
                          className="student-avatar"
                        />
                        <Typography variant="body2" className="name-text">
                          {session.studentName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{session.date}</TableCell>
                    <TableCell>{session.timing}</TableCell>
                    <TableCell>{session.subject}</TableCell>
                    <TableCell>{formatLevel(session.level)}</TableCell>
                    <TableCell>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          color: typeInfo.color,
                        }}
                      >
                        <img
                          src={typeInfo.icon}
                          alt={typeInfo.value}
                          style={{ width: 20, height: 20 }}
                        />
                        <Typography variant="body2" component="span" className="level-text">
                          {typeInfo.value || session.sessionType}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant={session.canJoin ? 'contained' : 'outlined'}
                        className={session.canJoin ? 'join-button active' : 'join-button disabled'}
                        size="small"
                        disabled={!session.canJoin}
                      >
                        Join
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ padding: '30px 16px' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    No upcoming sessions found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Only show pagination if there are sessions */}
      {currentRows.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <nav>
            <List>
              {items.map(({ page: itemPage, type, selected, ...item }, index) => {
                let children = null;
                if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                  children = '…';
                } else if (type === 'page') {
                  children = (
                    <button
                      type="button"
                      style={{
                        margin: '0 5px',
                        padding: '5px 10px',
                        cursor: 'pointer',
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        lineHeight: 'normal',
                        color: '#000',
                        backgroundColor: selected ? '#f0f0f0' : 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                      }}
                      {...item}
                    >
                      {itemPage}
                    </button>
                  );
                } else {
                  children = (
                    <button
                      type="button"
                      style={{
                        margin: '0 5px',
                        padding: '5px 10px',
                        cursor: 'pointer',
                        fontFamily: 'Poppins',
                        fontSize: '12px',
                        fontStyle: 'normal',
                        fontWeight: '400',
                        lineHeight: 'normal',
                        color: '#000',
                        border: 'none',
                        backgroundColor: 'transparent',
                      }}
                      {...item}
                    >
                      {formatLevel(type)}
                    </button>
                  );
                }
                return <li key={index}>{children}</li>;
              })}
            </List>
          </nav>
        </Box>
      )}
    </Box>
  );
};

UpcomingSessionTable.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      studentName: PropTypes.string.isRequired,
      avatarUrl: PropTypes.string,
      date: PropTypes.string.isRequired,
      timing: PropTypes.string.isRequired,
      subject: PropTypes.string.isRequired,
      level: PropTypes.string.isRequired,
      sessionType: PropTypes.string.isRequired,
      canJoin: PropTypes.bool.isRequired,
    })
  ),
};

UpcomingSessionTable.defaultProps = {
  sessions: [],
};

export default UpcomingSessionTable;
